import { defineStore } from 'pinia'
import { ref } from 'vue'
import { usePickemApi } from '~/services/api'
import type {
  Stage,
  PickemTeam,
  SwissCategory,
  SwissPrediction,
  PredictionMutationResponse,
  BracketMatch
} from '../types'
import { useStageStore } from './stage'
import { useTournamentStore } from './tournament'
import { generateIdempotencyKey } from '../composables/use-idempotency-key'
import {
  usePickemAnalytics,
  type PickemPickAnalyticsMeta
} from '../composables/use-pickem-analytics'
import { usePickemAuth } from '../composables/use-pickem-auth'

// Прогнозы юзера: оптимистичные апдейты + откат при ошибке.
// Ставит ячейку в «inFlight», пока не пришёл ответ сервера.
//
// Идемпотентность: для каждой пары (stage_id, team_id) храним один ключ —
// если предыдущий ретрай ещё не завершился, новый клик использует тот же ключ
// (бэк ответит закешированным ответом).

interface InFlightSwiss {
  stageId: number
  teamId: number
}

interface InFlightBracket {
  stageId: number
  matchId: number
}

const swissPendingKey = (stageId: number, teamId: number) =>
  `swiss:${stageId}:${teamId}`
const bracketPendingKey = (stageId: number, matchId: number) =>
  `bracket:${stageId}:${matchId}`

export const usePredictionsStore = defineStore('pickem-predictions', () => {
  const { trackPickSuccess } = usePickemAnalytics()
  const tournamentStore = useTournamentStore()
  const pickemApi = usePickemApi()
  const { ensureAuthorized } = usePickemAuth()

  // Ключ → текущий idempotency-key. Чистится после успешного ответа.
  const idempotencyMap = new Map<string, string>()
  // Сет «in-flight» операций — для disable-стейта в UI.
  const inFlightSwiss = ref<Set<string>>(new Set())
  const inFlightBracket = ref<Set<string>>(new Set())

  const getOrCreateKey = (mapKey: string): string => {
    let key = idempotencyMap.get(mapKey)
    if (!key) {
      key = generateIdempotencyKey()
      idempotencyMap.set(mapKey, key)
    }
    return key
  }

  const clearKey = (mapKey: string) => {
    idempotencyMap.delete(mapKey)
  }

  const isSwissInFlight = (stageId: number, teamId: number) =>
    inFlightSwiss.value.has(swissPendingKey(stageId, teamId))

  const isBracketInFlight = (stageId: number, matchId: number) =>
    inFlightBracket.value.has(bracketPendingKey(stageId, matchId))

  // -------------------------- swiss --------------------------

  const upsertSwiss = async ({
    stage,
    team,
    category,
    analyticsMeta
  }: {
    stage: Stage
    team: PickemTeam
    category: SwissCategory
    analyticsMeta?: PickemPickAnalyticsMeta
  }): Promise<PredictionMutationResponse | null> => {
    if (!stage.swiss) return null

    const stageStore = useStageStore()
    const mapKey = swissPendingKey(stage.id, team.id)
    const key = getOrCreateKey(mapKey)
    const previousPrediction = stage.swiss.predictions.find(
      p => p.team_id === team.id
    )

    try {
      await ensureAuthorized()
    } catch {
      return null
    }

    // Оптимистично двигаем команду в категорию (или ставим, если нет).
    const optimistic = applyOptimisticSwissUpsert(stage, team.id, category.id)
    inFlightSwiss.value = new Set(inFlightSwiss.value).add(mapKey)

    try {
      const response = await pickemApi.upsertSwissPrediction(
        { stage_id: stage.id, team_id: team.id, category_id: category.id },
        key
      )

      stageStore.updateData({
        prediction_status: response.stage.prediction_status,
        slots_filled: response.stage.slots_filled,
        slots_total: response.stage.slots_total
      } as Partial<Stage>)

      clearKey(mapKey)
      if (analyticsMeta && tournamentStore.tournament) {
        trackPickSuccess(analyticsMeta, tournamentStore.tournament)
      }
      return response
    } catch (error) {
      // Откат к предыдущему состоянию.
      revertOptimisticSwiss(
        stage,
        team.id,
        optimistic,
        previousPrediction ?? null
      )
      throw error
    } finally {
      const next = new Set(inFlightSwiss.value)
      next.delete(mapKey)
      inFlightSwiss.value = next
    }
  }

  const removeSwiss = async ({
    stage,
    team,
    analyticsMeta
  }: {
    stage: Stage
    team: PickemTeam
    analyticsMeta?: PickemPickAnalyticsMeta
  }): Promise<PredictionMutationResponse | null> => {
    if (!stage.swiss) return null

    const stageStore = useStageStore()
    const mapKey = swissPendingKey(stage.id, team.id)
    const key = getOrCreateKey(mapKey)
    const previousPrediction = stage.swiss.predictions.find(
      p => p.team_id === team.id
    )
    if (!previousPrediction) return null

    try {
      await ensureAuthorized()
    } catch {
      return null
    }

    applyOptimisticSwissRemove(stage, team.id)
    inFlightSwiss.value = new Set(inFlightSwiss.value).add(mapKey)

    try {
      const response = await pickemApi.removeSwissPrediction(
        { stage_id: stage.id, team_id: team.id },
        key
      )

      stageStore.updateData({
        prediction_status: response.stage.prediction_status,
        slots_filled: response.stage.slots_filled,
        slots_total: response.stage.slots_total
      } as Partial<Stage>)

      clearKey(mapKey)
      if (analyticsMeta && tournamentStore.tournament) {
        trackPickSuccess(analyticsMeta, tournamentStore.tournament)
      }
      return response
    } catch (error) {
      // Возвращаем команду в исходную категорию.
      stage.swiss?.predictions.push(previousPrediction)
      throw error
    } finally {
      const next = new Set(inFlightSwiss.value)
      next.delete(mapKey)
      inFlightSwiss.value = next
    }
  }

  // -------------------------- bracket --------------------------

  const upsertBracket = async ({
    stage,
    match,
    team,
    analyticsMeta
  }: {
    stage: Stage
    match: BracketMatch
    team: PickemTeam
    analyticsMeta?: PickemPickAnalyticsMeta
  }): Promise<PredictionMutationResponse | null> => {
    if (!stage.bracket) return null

    const stageStore = useStageStore()
    const mapKey = bracketPendingKey(stage.id, match.id)
    const key = getOrCreateKey(mapKey)
    const previous = match.user_pick_team_id

    try {
      await ensureAuthorized()
    } catch {
      return null
    }

    match.user_pick_team_id = team.id
    inFlightBracket.value = new Set(inFlightBracket.value).add(mapKey)

    try {
      const response = await pickemApi.upsertBracketPrediction(
        { stage_id: stage.id, match_id: match.id, team_id: team.id },
        key
      )

      stageStore.updateData({
        prediction_status: response.stage.prediction_status,
        slots_filled: response.stage.slots_filled,
        slots_total: response.stage.slots_total
      } as Partial<Stage>)

      await stageStore.fetchItem(stage.id)

      clearKey(mapKey)
      if (analyticsMeta && tournamentStore.tournament) {
        trackPickSuccess(analyticsMeta, tournamentStore.tournament)
      }
      return response
    } catch (error) {
      match.user_pick_team_id = previous
      throw error
    } finally {
      const next = new Set(inFlightBracket.value)
      next.delete(mapKey)
      inFlightBracket.value = next
    }
  }

  const removeBracket = async ({
    stage,
    match,
    analyticsMeta
  }: {
    stage: Stage
    match: BracketMatch
    analyticsMeta?: PickemPickAnalyticsMeta
  }): Promise<PredictionMutationResponse | null> => {
    if (!stage.bracket) return null

    const stageStore = useStageStore()
    const mapKey = bracketPendingKey(stage.id, match.id)
    const key = getOrCreateKey(mapKey)
    const previous = match.user_pick_team_id

    try {
      await ensureAuthorized()
    } catch {
      return null
    }

    match.user_pick_team_id = null
    inFlightBracket.value = new Set(inFlightBracket.value).add(mapKey)

    try {
      const response = await pickemApi.removeBracketPrediction(
        { stage_id: stage.id, match_id: match.id },
        key
      )

      stageStore.updateData({
        prediction_status: response.stage.prediction_status,
        slots_filled: response.stage.slots_filled,
        slots_total: response.stage.slots_total
      } as Partial<Stage>)

      await stageStore.fetchItem(stage.id)

      clearKey(mapKey)
      if (analyticsMeta && tournamentStore.tournament) {
        trackPickSuccess(analyticsMeta, tournamentStore.tournament)
      }
      return response
    } catch (error) {
      match.user_pick_team_id = previous
      throw error
    } finally {
      const next = new Set(inFlightBracket.value)
      next.delete(mapKey)
      inFlightBracket.value = next
    }
  }

  return {
    isSwissInFlight,
    isBracketInFlight,
    upsertSwiss,
    removeSwiss,
    upsertBracket,
    removeBracket
  }
})

// =====================================================================
// Helpers — оптимистичные апдейты Swiss-стейта
// =====================================================================

interface OptimisticSwissResult {
  inserted: boolean
  movedFromCategoryId: number | null
}

const applyOptimisticSwissUpsert = (
  stage: Stage,
  teamId: number,
  categoryId: number
): OptimisticSwissResult => {
  if (!stage.swiss) return { inserted: false, movedFromCategoryId: null }

  const existing = stage.swiss.predictions.find(p => p.team_id === teamId)
  if (existing) {
    const movedFrom = existing.category_id
    existing.category_id = categoryId
    return { inserted: false, movedFromCategoryId: movedFrom }
  }

  stage.swiss.predictions.push({
    team_id: teamId,
    category_id: categoryId,
    is_correct: null
  })
  return { inserted: true, movedFromCategoryId: null }
}

const applyOptimisticSwissRemove = (stage: Stage, teamId: number) => {
  if (!stage.swiss) return
  const idx = stage.swiss.predictions.findIndex(p => p.team_id === teamId)
  if (idx >= 0) stage.swiss.predictions.splice(idx, 1)
}

// При ошибке возвращаем prediction в исходное состояние:
// - если был — восстанавливаем category_id;
// - если не было (вставка свежая) — удаляем.
const revertOptimisticSwiss = (
  stage: Stage,
  teamId: number,
  optimistic: OptimisticSwissResult,
  previous: SwissPrediction | null
) => {
  if (!stage.swiss) return
  const idx = stage.swiss.predictions.findIndex(p => p.team_id === teamId)

  if (optimistic.inserted) {
    if (idx >= 0) stage.swiss.predictions.splice(idx, 1)
    return
  }

  if (idx >= 0 && previous) {
    stage.swiss.predictions[idx] = { ...previous }
  }
}
